import moment from 'moment';
import {lazy} from 'react';

import EntryActions from 'actions/EntryActions';
import NoteActions from 'actions/NoteActions';
import BenchlingConfig from 'BenchlingConfig';
import * as benchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import SubtemplateActions from 'notebook/entry-templates/SubtemplateActions';
import TemplateActions from 'notebook/entry-templates/TemplateActions';
// @ts-expect-error
import EditorActions from 'notebook/protocols/actions/EditorActions';
import DatadogUtil from 'performance/DatadogUtil';
import APIUtil from 'util/APIUtil';
import {renderReactComponent} from 'util/RootReactRender';
import suspend from 'util/suspend';
import * as StageEntryActions from 'workflows/stage-entries/StageEntryActions';

type QueryKey = Record<string, string>;

interface IndexSlice {
  /** Appears to be used to delimit the part of a notebook entry or template that gets printed. */
  start: number;
  end: number;
}

export default function printerFriendlyViewHandler(): void {
  const id = window.bootstrap!.itemId;
  const {queryKey} = benchlingUtil.parseUri(window.location.href);
  const {itemName} = window.bootstrap!;

  renderView(id, queryKey, itemName);
}

const EntryPrinterFriendlyView = lazy(() =>
  import(/* webpackChunkName: "printer-friendly" */ 'notebook/entries/PrinterFriendlyView').then((m) => ({
    default: m.EntryPrinterFriendlyView,
  }))
);
const WorksheetPrinterFriendlyView = lazy(() =>
  import(/* webpackChunkName: "printer-friendly" */ 'notebook/entries/PrinterFriendlyView').then((m) => ({
    default: m.WorksheetPrinterFriendlyView,
  }))
);
const TemplatePrinterFriendlyView = lazy(() =>
  import(/* webpackChunkName: "printer-friendly" */ 'notebook/entries/PrinterFriendlyView').then((m) => ({
    default: m.TemplatePrinterFriendlyView,
  }))
);
const SubtemplatePrinterFriendlyView = lazy(() =>
  import(/* webpackChunkName: "printer-friendly" */ 'notebook/entries/PrinterFriendlyView').then((m) => ({
    default: m.SubtemplatePrinterFriendlyView,
  }))
);
const NotePrintableViewClass = lazy(
  () => import(/* webpackChunkName: "printer-friendly" */ 'notebook/bioentities/NotePrintableView')
);
const ProtocolPrintableView = lazy(
  () => import(/* webpackChunkName: "printer-friendly" */ 'notebook/protocols/ProtocolPrintableView')
);
const StageEntryPrintView = lazy(
  () => import(/* webpackChunkName: "printer-friendly" */ 'workflows/stage-entries/StageEntryPrintView')
);

// Exported only for testing.
/*
TODO(BNCH-51863) break up this function into one function per `switch` case, and move each to the
appropriate route-bundle file.
*/
export function renderView(id: string, queryKey: QueryKey, itemName: string): void {
  DatadogUtil.startFolderItemViewerRUMView(id);
  // default to latest if no versionId is specified
  const versionId = queryKey.versionId != null ? parseInt(queryKey.versionId, 10) : undefined;
  switch (APIUtil.apiIdToType(id)) {
    case ModelTypeConstants.ENTRY:
      // expects the query to look like ?note=87&protocols=8,7,2
      EntryActions.open(id, {versionId});
      renderReactComponent(suspend(EntryPrinterFriendlyView), getPropsForPrinterFriendlyView(queryKey, id));
      break;
    case ModelTypeConstants.WORKSHEET:
      renderReactComponent(suspend(WorksheetPrinterFriendlyView), {id});
      break;
    case ModelTypeConstants.TEMPLATE:
      TemplateActions.open(id, {versionId});
      renderReactComponent(
        suspend(TemplatePrinterFriendlyView),
        getPropsForPrinterFriendlyView(queryKey, id)
      );
      break;
    case ModelTypeConstants.SUBTEMPLATE:
      SubtemplateActions.open(id, {versionId});
      renderReactComponent(
        suspend(SubtemplatePrinterFriendlyView),
        getPropsForPrinterFriendlyView(queryKey, id)
      );
      break;
    case ModelTypeConstants.PROTOCOL:
      if (versionId != null) {
        EditorActions.loadVersion(id, versionId);
      } else {
        EditorActions.open(id);
      }
      renderReactComponent(suspend(ProtocolPrintableView), {
        id,
      });
      break;
    case ModelTypeConstants.STAGE_ENTRY:
      StageEntryActions.fetchEntryDetails(id);
      // wrap in Suspense: renderReactComponent(StageEntryPrintView, getPropsForPrinterFriendlyView(queryKey, id));
      renderReactComponent(suspend(StageEntryPrintView), getPropsForPrinterFriendlyView(queryKey, id));
      break;
    default:
      NoteActions.loadVersion(id, queryKey.note);
      renderReactComponent(suspend(NotePrintableViewClass), {
        id,
        title: itemName,
      });
  }
}

// Exported only for testing.
export function getPropsForPrinterFriendlyView(
  queryKey: QueryKey,
  id: string
): {
  id: string;
  showNote: boolean;
  showProtocols: Array<number>;
  indexSlice: IndexSlice;
  showTimestamps: boolean;
  exportConfig:
    | {showEntryTitle: boolean; showFirstSectionHeader: boolean; showGeneratedOnTimestamp: boolean}
    | undefined;
  reviewChangeDate?: moment.Moment;
} {
  const protocolIds = (queryKey.protocols && decodeURIComponent(queryKey.protocols).split(',')) || [];
  const exportConfig =
    queryKey.showEntryTitle != null ||
    queryKey.showFirstSectionHeader != null ||
    queryKey.showGeneratedOnTimestamp != null
      ? {
          showEntryTitle: queryKey.showEntryTitle != null ? queryKey.showEntryTitle === 'true' : true,
          showFirstSectionHeader:
            queryKey.showFirstSectionHeader != null ? queryKey.showFirstSectionHeader === 'true' : true,
          showGeneratedOnTimestamp:
            queryKey.showGeneratedOnTimestamp != null ? queryKey.showGeneratedOnTimestamp === 'true' : true,
        }
      : undefined;
  // Used to override single input tables for the purpose of Export visual comparisons
  if (queryKey.singleInput != null) {
    BenchlingConfig.ELN_ENABLE_STRUCTURED_TABLE_COLUMN_SINGLE_INPUTS = queryKey.singleInput === 'true';
  }
  const props = {
    id,
    showNote: queryKey.note === 'true',
    showProtocols: protocolIds.map((x) => parseInt(x, 10)),
    indexSlice: getIndexSliceForPrinterFriendlyView(queryKey),
    showTimestamps: queryKey.showTimestamps === 'true',
    exportConfig,
    reviewChangeDate:
      queryKey.reviewChangeDate != null ? moment(decodeURIComponent(queryKey.reviewChangeDate)) : undefined,
  };
  return props;
}

// Exported only for testing.
export function getIndexSliceForPrinterFriendlyView(queryKey: QueryKey): IndexSlice {
  const indexSlice = {
    start: queryKey.startIndex != null ? parseInt(queryKey.startIndex, 10) : 0,
    end: queryKey.endIndex != null ? parseInt(queryKey.endIndex, 10) : Infinity,
  };
  return indexSlice;
}
